


































































import { mapGetters, mapState } from 'vuex';
import * as vr from '@/helpers/validation';
import {
  SelectItem,
  TStreamConfigurationPayload,
  TStreamConfigurationResponse
} from '@/api/schema';
import { ECopyStakePackageProgram } from '@/api/schema';

import {
  getStreamConfiguration,
  updateStreamConfiguration
} from '@/api/CopyStake/Configuration';
import { ECopyStakeFeatureFlag } from '@/store/modules/feature-flags/FeatureFlagNames';
import { cloneDeep } from 'lodash';
import { errorToastMessage } from '@/helpers/errorToastMessage';

type SettingsCopyStakePackageViewData = {
  streamConfigurationSavedData: TStreamConfigurationResponse | null;
  loading: boolean;
  formData: {
    program: ECopyStakePackageProgram;
    streamersLimit: string;
    watchersLimit: string;
    webProgram: ECopyStakePackageProgram;
    webStreamersLimit: string;
    webWatchersLimit: string;
  };
  areLimitsFieldsEditable: boolean;
  areWebLimitsFieldsEditable: boolean;
  isFormValid: boolean;
};

export default {
  name: 'SettingsCopyStakePackageView',
  data(): SettingsCopyStakePackageViewData {
    return {
      loading: true,
      areLimitsFieldsEditable: true,
      areWebLimitsFieldsEditable: true,
      isFormValid: false,

      streamConfigurationSavedData: null,

      formData: {
        program: ECopyStakePackageProgram.MINI_PROVISION,
        streamersLimit: '0',
        watchersLimit: '0',
        webProgram: ECopyStakePackageProgram.MINI_PROVISION,
        webStreamersLimit: '0',
        webWatchersLimit: '0'
      }
    };
  },
  computed: {
    ...mapState('SuperAdmin', ['operator']),
    ...mapGetters('FeatureFlags', ['isEnabledFeatureFlag']),
    isWebStreamersFeatureEnabled(): boolean {
      return this.isEnabledFeatureFlag(ECopyStakeFeatureFlag.WEB_STREAMERS);
    },
    isFormDisabled(): boolean {
      return this.loading;
    },
    rules(): Record<string, unknown[]> {
      return {
        streamersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        watchersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        webStreamersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ],
        webWatchersLimit: [
          vr.required,
          vr.validNumber,
          vr.integer,
          vr.positiveNumber
        ]
      };
    },
    programs(): SelectItem[] {
      return [
        {
          text: 'Mini Provision',
          value: ECopyStakePackageProgram.MINI_PROVISION
        },
        {
          text: 'Standard Provision',
          value: ECopyStakePackageProgram.STANDARD_PROVISION
        },
        {
          text: 'Enterprise',
          value: ECopyStakePackageProgram.ENTERPRISE
        }
      ];
    },
    programsSettingMap(): Record<
      ECopyStakePackageProgram,
      {
        streamersLimit: number | null;
        watchersLimit: number | null;
        areLimitsFieldsEditable: boolean;
      }
    > {
      return {
        [ECopyStakePackageProgram.MINI_PROVISION]: {
          streamersLimit: 2,
          watchersLimit: 500,
          areLimitsFieldsEditable: false
        },
        [ECopyStakePackageProgram.STANDARD_PROVISION]: {
          streamersLimit: 5,
          watchersLimit: 2000,
          areLimitsFieldsEditable: false
        },
        [ECopyStakePackageProgram.ENTERPRISE]: {
          streamersLimit: null,
          watchersLimit: null,
          areLimitsFieldsEditable: true
        }
      };
    }
  },
  created(): void {
    this.loadCopyStakePackage();
  },
  watch: {
    operator: {
      handler(): void {
        this.loadCopyStakePackage();
      },
      deep: true
    },
    streamConfigurationSavedData: {
      handler(): void {
        if (this.streamConfigurationSavedData === null) {
          return;
        }

        this.setupFormData();
        this.setupWebFormData();

        this.$refs.form?.resetValidation();
      },
      deep: true
    },
    'formData.program': {
      handler(program: ECopyStakePackageProgram): void {
        const { streamersLimit, watchersLimit, areLimitsFieldsEditable } =
          this.programsSettingMap[program];

        this.formData.streamersLimit = `${
          streamersLimit ??
          this.streamConfigurationSavedData.limits.streamers ??
          0
        }`;
        this.formData.watchersLimit = `${
          watchersLimit ??
          this.streamConfigurationSavedData.limits.watchers ??
          0
        }`;
        this.areLimitsFieldsEditable = areLimitsFieldsEditable;

        this.$refs.form?.resetValidation();
      },
      deep: true
    },
    'formData.webProgram': {
      handler(webProgram: ECopyStakePackageProgram): void {
        const { streamersLimit, watchersLimit, areLimitsFieldsEditable } =
          this.programsSettingMap[webProgram];

        this.formData.webStreamersLimit = `${
          streamersLimit ??
          this.streamConfigurationSavedData.limits.webStreamers ??
          0
        }`;
        this.formData.webWatchersLimit = `${
          watchersLimit ??
          this.streamConfigurationSavedData.limits.webWatchers ??
          0
        }`;
        this.areWebLimitsFieldsEditable = areLimitsFieldsEditable;

        this.$refs.form?.resetValidation();
      },
      deep: true
    }
  },
  methods: {
    setupFormData(): void {
      if (
        this.streamConfigurationSavedData.limits.streamers === null ||
        this.streamConfigurationSavedData.limits.watchers === null
      ) {
        this.formData.program = ECopyStakePackageProgram.MINI_PROVISION;
        this.formData.streamersLimit = 2;
        this.formData.watchersLimit = 500;

        return;
      }

      this.formData.streamersLimit = `${
        this.streamConfigurationSavedData.limits.streamers ?? 0
      }`;
      this.formData.watchersLimit = `${
        this.streamConfigurationSavedData.limits.watchers ?? 0
      }`;
      this.formData.program = ECopyStakePackageProgram.ENTERPRISE;

      for (const program in this.programsSettingMap) {
        const { streamersLimit, watchersLimit } =
          this.programsSettingMap[program];

        if (
          this.streamConfigurationSavedData.limits.streamers ===
            streamersLimit &&
          this.streamConfigurationSavedData.limits.watchers === watchersLimit
        ) {
          this.formData.program = program;

          break;
        }
      }
    },
    setupWebFormData(): void {
      if (
        this.streamConfigurationSavedData.limits.webStreamers === null ||
        this.streamConfigurationSavedData.limits.webWatchers === null
      ) {
        this.formData.webProgram = ECopyStakePackageProgram.MINI_PROVISION;
        this.formData.webStreamersLimit = 2;
        this.formData.webWatchersLimit = 500;

        return;
      }

      this.formData.webStreamersLimit = `${
        this.streamConfigurationSavedData.limits.webStreamers ?? 0
      }`;
      this.formData.webWatchersLimit = `${
        this.streamConfigurationSavedData.limits.webWatchers ?? 0
      }`;
      this.formData.webProgram = ECopyStakePackageProgram.ENTERPRISE;

      for (const program in this.programsSettingMap) {
        const { streamersLimit, watchersLimit } =
          this.programsSettingMap[program];

        if (
          this.streamConfigurationSavedData.limits.webStreamers ===
            streamersLimit &&
          this.streamConfigurationSavedData.limits.webWatchers ===
            watchersLimit
        ) {
          this.formData.webProgram = program;

          break;
        }
      }
    },
    resetComponentState(): void {
      this.loading = true;
      this.streamConfigurationSavedData = null;
      this.formData = {
        program: ECopyStakePackageProgram.MINI_PROVISION,
        streamersLimit: '2',
        watchersLimit: '500',
        webProgram: ECopyStakePackageProgram.MINI_PROVISION,
        webStreamersLimit: '2',
        webWatchersLimit: '500'
      };
      this.areLimitsFieldsEditable = true;
      this.areWebLimitsFieldsEditable = true;
      this.isFormValid = false;
      this.$refs.form?.resetValidation();
    },
    async loadCopyStakePackage(): Promise<void> {
      try {
        this.resetComponentState();

        await getStreamConfiguration()
          .then((data: TStreamConfigurationResponse) => {
            this.streamConfigurationSavedData = cloneDeep(data);
          })
          .finally(() => {
            this.loading = false;
          });
      } catch (error) {
        this.$toast.error('Something was wrong', error);
      }
    },
    async submitHandler(): Promise<void> {
      if (this.isFormDisabled) {
        return;
      }

      const form = this.$refs.form;
      form.validate();

      await this.$nextTick();

      if (!this.isFormValid) {
        return;
      }

      this.loading = true;

      const payload: Partial<TStreamConfigurationPayload> = cloneDeep(
        this.streamConfigurationSavedData
      );

      payload.limits.streamers = +this.formData.streamersLimit;
      payload.limits.watchers = +this.formData.watchersLimit;

      if (this.isWebStreamersFeatureEnabled) {
        payload.limits.webStreamers = +this.formData.webStreamersLimit;
        payload.limits.webWatchers = +this.formData.webWatchersLimit;
      }

      await updateStreamConfiguration(payload)
        .then((data: TStreamConfigurationResponse) => {
          this.resetComponentState();
          this.streamConfigurationSavedData = cloneDeep(data);

          this.$toast.success('CopyStake packages successfully updated!');
        })
        .catch(errorToastMessage)
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
